/**
 * Add Topic Modal Component
 * Form for creating new library topics
 */

import { useState, useCallback } from "react";
import { Modal } from "./Modal";
import { Button } from "@components/common";
import type { CreateTopicRequest } from "@/types/models";

interface AddTopicModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTopicRequest) => Promise<void>;
  isLoading: boolean;
  error?: string | null;
}

function generateSlug(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function AddTopicModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  error,
}: AddTopicModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Topic"
      description="Create a new topic for categorizing library resources"
      size="md"
    >
      {/* Only render form content when modal is open - this ensures fresh state on each open */}
      {isOpen && (
        <AddTopicFormContent
          onSubmit={onSubmit}
          onClose={onClose}
          isLoading={isLoading}
          error={error}
        />
      )}
    </Modal>
  );
}

interface AddTopicFormContentProps {
  onSubmit: (data: CreateTopicRequest) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
  error?: string | null;
}

/**
 * Form content component that manages state and renders both form and footer.
 * Only mounted when modal isOpen, so state resets naturally on close/reopen.
 */
function AddTopicFormContent({
  onSubmit,
  onClose,
  isLoading,
  error,
}: AddTopicFormContentProps) {
  const [label, setLabel] = useState("");
  const [manualSlug, setManualSlug] = useState<string | null>(null);
  const [description, setDescription] = useState("");

  // Compute slug: use manual slug if set, otherwise auto-generate from label
  const slug = manualSlug ?? generateSlug(label);
  const autoSlug = manualSlug === null;

  const handleSlugChange = useCallback((value: string) => {
    setManualSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
  }, []);

  const isValid = label.trim().length > 0 && slug.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard against duplicate submissions (e.g., Enter key while request in flight)
    if (isLoading || !isValid) return;
    await onSubmit({
      label: label.trim(),
      slug: slug.trim(),
      description: description.trim() || undefined,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Error message */}
        {error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Label */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Label *
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={100}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g., RAG Pipelines"
          />
          <p className="mt-1 text-xs text-gray-500">
            Display name shown in filters and forms
          </p>
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Slug *
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            required
            maxLength={50}
            pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g., rag-pipelines"
          />
          <p className="mt-1 text-xs text-gray-500">
            URL-friendly identifier (lowercase, hyphens only).{" "}
            {autoSlug ? "Auto-generated from label." : "Manually edited."}
          </p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Optional description of this topic..."
          />
        </div>
      </form>

      {/* Footer */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isValid || isLoading}
          isLoading={isLoading}
        >
          Create Topic
        </Button>
      </div>
    </div>
  );
}
